/**
 * Orders Snapshot — periodic structured log emission for diagnosing issue #317
 * (positions stop updating intermittently).
 *
 * Companion to `clientSnapshot.js` (which covers marketdata/quote health).
 * This snapshot captures the orders-router internal state:
 *   - throttle state machine (pending notify, timeout active, etc.)
 *   - dedup state (last accountData string length)
 *   - data presence (positions / open orders / balances counts)
 *   - connection lifecycle (receivers count, reconnect attempts)
 *   - browser-level focus signal (document.hidden, document.hasFocus())
 *   - rolling counters since last snapshot (deduphits/misses, throttle states,
 *     max actual setTimeout delay — smoking gun for browser wake-up throttling)
 *
 * Volume control:
 *   - Diff-based emission: only ships when state OR rolling deltas changed
 *     since the last shipped snapshot.
 *   - Heartbeat: forces a snapshot every HEARTBEAT_INTERVAL_MS even with no
 *     change, so we can distinguish "truly idle" from "client died".
 *   - Whitelist-gated via the existing logger → logBatcher pipeline.
 *
 * Consumed server-side by querying `client_debug_logs` for entries with
 * `Message: "[OrdersSnapshot]"` for a given UserId in the time window of
 * interest.
 */

import { router } from "../data/Services/ordersRouter";
import logger from "./logger";

const SNAPSHOT_INTERVAL_MS = 15000; // 15s — fine resolution for #317 timeline correlation
const HEARTBEAT_INTERVAL_MS = 1800000; // 30 min — forced ship even with no change (alive signal)

let intervalId = null;
let lastStateSignature = null;
let lastShippedAt = 0;

function buildState() {
  const now = Date.now();
  const lastNotifyAgo = router.lastAccountDataNotifyTime
    ? now - router.lastAccountDataNotifyTime
    : null;

  const positionsCount = router.accountData?.positions
    ? Object.keys(router.accountData.positions).length
    : 0;
  const openOrdersCount = router.accountData?.openOrders
    ? Object.keys(router.accountData.openOrders).reduce(
        (sum, sym) => sum + (router.accountData.openOrders[sym]?.length || 0),
        0,
      )
    : 0;
  const balancesCount = router.accountData?.balances
    ? Array.isArray(router.accountData.balances)
      ? router.accountData.balances.length
      : Object.keys(router.accountData.balances).length
    : 0;

  return {
    // Connection lifecycle
    routerState: router.state,
    manualLogout: !!router.manualLogout,
    idle: !!router.idle,
    closedByServer: !!router.closedByServer,
    reconnectionAttempts: router.reconnectionAttempts || 0,
    broker: router.broker || null,

    // Throttle state machine
    pendingAccountDataNotify: !!router.pendingAccountDataNotify,
    accountDataNotifyTimeoutActive: !!router.accountDataNotifyTimeout,
    pendingAccountDataPresent: !!router.pendingAccountData,
    lastAccountDataNotifyAgoMs: lastNotifyAgo,
    forceImmediateAccountData: !!router._forceImmediateAccountData,

    // Dedup
    lastAccountDataStringLen: router._lastAccountDataString?.length || 0,

    // Data presence
    positionsCount,
    openOrdersCount,
    balancesCount,
    registeredPositionSymbolsCount:
      router._registeredPositionSymbols?.size || 0,

    // Receivers
    receiversCount: Array.isArray(router.receivers)
      ? router.receivers.length
      : 0,

    // Browser-level focus signal
    documentHidden: typeof document !== "undefined" ? document.hidden : null,
    documentHasFocus:
      typeof document !== "undefined" && typeof document.hasFocus === "function"
        ? document.hasFocus()
        : null,
    documentVisibilityState:
      typeof document !== "undefined" ? document.visibilityState : null,
  };
}

function consumeDeltas() {
  const d = router._diag;
  if (!d) {
    return {
      accountDataReceived: 0,
      dedupHits: 0,
      dedupMisses: 0,
      throttleImmediate: 0,
      throttleScheduled: 0,
      throttleAccumulated: 0,
      throttleTimeoutFired: 0,
      throttleMaxActualDelayMs: 0,
      throttleSlowTimeoutCount: 0,
    };
  }
  const snapshot = { ...d };
  // Reset rolling counters after consuming so the next snapshot represents
  // only what happened in the next window.
  d.accountDataReceived = 0;
  d.dedupHits = 0;
  d.dedupMisses = 0;
  d.throttleImmediate = 0;
  d.throttleScheduled = 0;
  d.throttleAccumulated = 0;
  d.throttleTimeoutFired = 0;
  d.throttleMaxActualDelayMs = 0;
  d.throttleSlowTimeoutCount = 0;
  return snapshot;
}

function stateSignature(state) {
  // Comparable fields only — excludes time-sensitive values that would
  // never match across snapshots (lastAccountDataNotifyAgoMs).
  return JSON.stringify({
    rs: state.routerState,
    ml: state.manualLogout,
    id: state.idle,
    cb: state.closedByServer,
    ra: state.reconnectionAttempts,
    br: state.broker,
    pn: state.pendingAccountDataNotify,
    tt: state.accountDataNotifyTimeoutActive,
    pp: state.pendingAccountDataPresent,
    fi: state.forceImmediateAccountData,
    ll: state.lastAccountDataStringLen,
    pc: state.positionsCount,
    oc: state.openOrdersCount,
    bc: state.balancesCount,
    rc: state.receiversCount,
    dh: state.documentHidden,
    df: state.documentHasFocus,
    dv: state.documentVisibilityState,
  });
}

function takeSnapshot() {
  try {
    // Skip entirely when router was never started (still idle on initial load)
    if (router.state === "offline" && router.idle && !router.broker) return;

    const state = buildState();
    const deltas = consumeDeltas();
    const sig = stateSignature(state);

    const hasActivity = Object.values(deltas).some((v) => v !== 0);
    const stateChanged = sig !== lastStateSignature;
    const heartbeatDue = Date.now() - lastShippedAt > HEARTBEAT_INTERVAL_MS;

    if (!hasActivity && !stateChanged && !heartbeatDue) {
      // Truly idle — same state, no activity, heartbeat not due.
      // Counters were already reset, but we'd lose them. Restore them
      // by accumulating into the next window: easiest is to just NOT
      // reset when we're not shipping. Re-add what we just consumed:
      const d = router._diag;
      if (d) {
        d.accountDataReceived += deltas.accountDataReceived;
        d.dedupHits += deltas.dedupHits;
        d.dedupMisses += deltas.dedupMisses;
        d.throttleImmediate += deltas.throttleImmediate;
        d.throttleScheduled += deltas.throttleScheduled;
        d.throttleAccumulated += deltas.throttleAccumulated;
        d.throttleTimeoutFired += deltas.throttleTimeoutFired;
        d.throttleSlowTimeoutCount += deltas.throttleSlowTimeoutCount;
        if (deltas.throttleMaxActualDelayMs > d.throttleMaxActualDelayMs) {
          d.throttleMaxActualDelayMs = deltas.throttleMaxActualDelayMs;
        }
      }
      return;
    }

    const reason = heartbeatDue
      ? "heartbeat"
      : stateChanged
        ? "state-change"
        : "activity";

    logger.diagnostic.info("[OrdersSnapshot]", {
      reason,
      ...state,
      deltas,
    });

    lastStateSignature = sig;
    lastShippedAt = Date.now();
  } catch (err) {
    console.warn("[OrdersSnapshot] failed to take snapshot", err);
  }
}

export function startOrdersSnapshot() {
  if (intervalId) return;
  intervalId = setInterval(takeSnapshot, SNAPSHOT_INTERVAL_MS);
  // Don't take an immediate snapshot on start — wait for the first tick so
  // there's data to compare against and to avoid noise during boot.
}

export function stopOrdersSnapshot() {
  if (intervalId) {
    clearInterval(intervalId);
    intervalId = null;
    lastStateSignature = null;
    lastShippedAt = 0;
  }
}
